// Shared shell — canonical site nav, page hero, reveal-on-scroll hook.
// SiteNav is used across every page (homepage, product, sub-pages) for a uniform
// link list and CTAs. Pass variant="transparent" on dark hero pages so the nav
// is transparent at the top of the viewport and turns solid on scroll.

import React from 'react';
import scopeworthLogoInverse from '../assets/scopeworth-logo-inverse.svg';
import { HeroBackground } from './HeroAndReport.jsx';
import { localizedHref } from './i18n/bootstrap.js';
import { t } from './i18n/useT.js';
import { appUrl } from './app-origin.js';
import { useSessionStatus } from './useSessionStatus.js';

export const SUB_PAGES = [
  { labelKey: 'nav.product', href: '/product' },
  { labelKey: 'nav.useCases', href: '/use-cases' },
  { labelKey: 'nav.pricing', href: '/pricing' },
  { labelKey: 'nav.integrations', href: '/integrations' },
];

export function SiteNavActions({ sessionStatus, mobile = false, onNavigate, middle }) {
  const authenticated = sessionStatus === 'authenticated';
  const className = mobile ? 'sw-btn sw-btn-primary' : 'sw-btn sw-btn-primary sw-btn-sm';
  if (authenticated) {
    return (
      <>
        {middle}
        <a href={appUrl('/app?resume=last-workspace')} className={className} onClick={onNavigate}>
          <span className="sw-btn-label">{t('nav.dashboard')}</span>
        </a>
      </>
    );
  }
  if (mobile) {
    return (
      <>
        <a href={appUrl('/sign-in')} className="sw-nav-mobile-link" onClick={onNavigate}>{t('nav.login')}</a>
        {middle}
        <a href={appUrl('/sign-up')} className={className} onClick={onNavigate}><span className="sw-btn-label">{t('nav.earlyAccess')}</span></a>
      </>
    );
  }
  return (
    <>
      <a href={appUrl('/sign-in')} className="sw-nav-link sw-nav-link-quiet">{t('nav.login')}</a>
      <a href={appUrl('/sign-up')} className={className}>
        <span className="sw-btn-label">{t('nav.earlyAccess')}</span>
      </a>
    </>
  );
}

export function SiteNav({ currentPath = '', variant = 'solid' }) {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const navRef = React.useRef(null);
  const sessionStatus = useSessionStatus();

  // Publish the bar's real height as --sw-nav-h. Heroes tuck under the nav with
  // a negative margin of exactly this height, so a stale constant shows a strip
  // of page background above the hero. The height genuinely varies: the burger
  // has a 44px touch target below 1200px, and locales with longer CTA labels can
  // grow it further. Measure the collapsed bar only — .sw-nav-mobile is a child,
  // so an open menu would otherwise report the whole drawer as nav height.
  React.useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const inner = nav.querySelector('.sw-nav-inner') || nav;
    const publish = () => {
      // Inner + the bar's own border-bottom: that sum is the nav's collapsed
      // outer height, which is what a hero has to clear.
      const border = parseFloat(getComputedStyle(nav).borderBottomWidth) || 0;
      const h = Math.round(inner.getBoundingClientRect().height + border);
      if (h > 0) document.documentElement.style.setProperty('--sw-nav-h', `${h}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  React.useEffect(() => {
    if (variant !== 'transparent') return;
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  const navClass = [
    'sw-nav',
    variant === 'solid' ? 'sw-nav-solid' : '',
    variant === 'transparent' && scrolled ? 'is-scrolled' : '',
    open ? 'is-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <nav className={navClass} ref={navRef} data-acquisition="free-account">
      <div className="sw-nav-inner">
        <a href={localizedHref('/')} className="sw-nav-logo" aria-label={t('nav.home')}>
          <img src={scopeworthLogoInverse} alt="ScopeWorth" height="32"/>
        </a>
        <div className="sw-nav-links">
          {SUB_PAGES.map(l => (
            <a
              key={l.href}
              href={localizedHref(l.href)}
              className={`sw-nav-link ${currentPath === l.href ? 'is-current' : ''}`}
              aria-current={currentPath === l.href ? 'page' : undefined}
            >
              {t(l.labelKey)}
            </a>
          ))}
        </div>
        <div className="sw-nav-actions">
          <SiteNavActions sessionStatus={sessionStatus} />
          <button className="sw-nav-burger" aria-label={t('nav.menu')} aria-expanded={open} onClick={() => setOpen(o => !o)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
          </button>
        </div>
      </div>
      {open && (
        <div className="sw-nav-mobile">
          {SUB_PAGES.map(l => (
            <a key={l.href} href={localizedHref(l.href)} className="sw-nav-mobile-link" onClick={() => setOpen(false)}>{t(l.labelKey)}</a>
          ))}
          <SiteNavActions
            sessionStatus={sessionStatus}
            mobile
            onNavigate={() => setOpen(false)}
          />
        </div>
      )}
    </nav>
  );
}

export function PageHero({ eyebrow, title, lead, children, visual, showcase = false, overlapNav = false }) {
  if (showcase) {
    return (
      <header className={`sw-page-hero sw-page-hero--showcase${visual ? ' has-visual' : ' is-copy-only'}${overlapNav ? ' is-nav-overlap' : ''}`}>
        <HeroBackground/>
        <div className="sw-container">
          <div className="sw-page-hero-showcase-inner">
            <div className="sw-page-hero-showcase-top">
              <div className="sw-page-hero-showcase-title">
                {eyebrow ? <div className="sw-eyebrow sw-eyebrow-teal"><span className="sw-eyebrow-dot"/>{eyebrow}</div> : null}
                <h1 className="sw-page-hero-title">{title}</h1>
              </div>
              <div className="sw-page-hero-showcase-support">
                {lead ? <p className="sw-page-hero-lead">{lead}</p> : null}
                {children ? <div className="sw-page-hero-actions">{children}</div> : null}
              </div>
            </div>
            {visual ? <div className="sw-page-hero-showcase-visual">{visual}</div> : null}
          </div>
        </div>
      </header>
    );
  }

  const copy = (
    <>
      {eyebrow ? <div className="sw-eyebrow sw-eyebrow-teal">{eyebrow}</div> : null}
      <h1 className="sw-page-hero-title">{title}</h1>
      {lead ? <p className="sw-page-hero-lead">{lead}</p> : null}
      {children ? <div className="sw-page-hero-actions">{children}</div> : null}
    </>
  );
  return (
    <header className={`sw-page-hero${visual ? ' sw-page-hero-split' : ''}`}>
      <HeroBackground/>
      <div className="sw-container">
        {visual ? (
          <div className="sw-page-hero-inner sw-page-hero-inner--split">
            <div className="sw-page-hero-copy">{copy}</div>
            <div className="sw-page-hero-visual" aria-hidden="true">{visual}</div>
          </div>
        ) : (
          <div className="sw-page-hero-inner">{copy}</div>
        )}
      </div>
    </header>
  );
}

export function PageCta({ source, title, lead, primary = 'sample', secondary }) {
  const onPrimary = () => {
    if (primary === 'sample') {
      window.openSampleReport && window.openSampleReport(source || 'page-cta');
    } else if (typeof primary === 'function') {
      primary();
    }
  };
  return (
    <section className="sw-section sw-section-dark sw-section-final" id="cta">
      <div className="sw-container">
        <div className="sw-final">
          <div className="sw-eyebrow sw-eyebrow-teal">{t('cta.eyebrow')}</div>
          <h2 className="sw-h-display">{title}</h2>
          {lead ? <p className="sw-lead sw-lead-dark">{lead}</p> : null}
          <div className="sw-cta-actions">
            <button className="sw-btn sw-btn-primary sw-btn-lg" onClick={onPrimary}>
              <span className="sw-btn-label">{t('cta.downloadSample')}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            {secondary ? (
              <a href={localizedHref(secondary.href)} className="sw-btn sw-btn-ghost-dark sw-btn-lg"><span className="sw-btn-label">{secondary.label}</span></a>
            ) : (
              <a href={localizedHref('/contact?reason=demo')} className="sw-btn sw-btn-ghost-dark sw-btn-lg"><span className="sw-btn-label">{t('hero.bookDemo')}</span></a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function useRevealOnScroll() {
  React.useEffect(() => {
    const els = document.querySelectorAll('.sw-reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function upsertMeta(selector, attribute, value, content) {
  let meta = document.head.querySelector(selector);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, value);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

export function setPageMeta({ title, description, canonical, image }) {
  if (typeof document === 'undefined') return;
  if (title) document.title = title;
  if (description) {
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement('meta');
      m.setAttribute('name', 'description');
      document.head.appendChild(m);
    }
    m.setAttribute('content', description);
  }
  if (canonical) {
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    if (title) {
      upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
      upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    }
    if (description) {
      upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
      upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }
  }
  if (image) {
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', image);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image);
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
  }
}
